/**
 * Plane-rooted route tree (INF-616 — R2.6).
 *
 * Two trees under /ui (basepath):
 *   /ui/platform/…  — Operations Console  (platform-plane modules)
 *   /ui/partner/…   — Partner Backoffice   (partner-plane modules)
 *
 * The root path "/" redirects to the correct plane based on the session's roles
 * (see IndexComponent / IndexRoute).
 *
 * Backwards-compatibility redirects for old paths are defined at the bottom.
 */

import { createElement, lazy, Suspense } from 'react';

import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router';

import { AppModuleRegistry } from '@bootstrap/app-registry';
import { requireAuthGuard } from '@bootstrap/auth/router-guards';
import APLoading from '@shared/components/molecules/loading';

import type { AuthClient } from '@bootstrap/auth/auth-client';
import type { TLayoutType, TPermissions, TRouteEntry } from '@modules/core/types';
import type { AnyRoute, RouteComponent } from '@tanstack/react-router';

import { useRouterContext } from './context';
import { isPartnerPlaneRoute, PANEL_BASEPATH, PARTNER_PREFIX, PLATFORM_PREFIX } from './plane';

// ---------------------------------------------------------------------------
// Lazy-loaded components
// ---------------------------------------------------------------------------
const RootComponent = lazy(() => import('@shared/components/pages/root-layout'));
const IndexComponent = lazy(() => import('@shared/components/pages/index-page'));
const NotFoundComponent = lazy(() => import('@shared/components/pages/not-found-page'));
const ForbiddenComponent = lazy(() => import('@shared/components/pages/forbidden-page'));

const DefaultLayout = lazy(() => import('@shared/components/templates/layouts/default-layout'));
const EmptyLayout = lazy(() => import('@shared/components/templates/layouts/empty-layout'));
const AuthLayout = lazy(() => import('@shared/components/templates/layouts/auth-layout'));

// ---------------------------------------------------------------------------
// Root route
// ---------------------------------------------------------------------------
export const rootRoute = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

// ---------------------------------------------------------------------------
// Layout routes (layout-only, no path — selected by the router based on route type)
// ---------------------------------------------------------------------------
export const defaultLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'default-layout',
  component: DefaultLayout,
  notFoundComponent: NotFoundComponent,
});

export const emptyLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'empty-layout',
  component: EmptyLayout as RouteComponent,
  notFoundComponent: NotFoundComponent,
});

export const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth-layout',
  component: AuthLayout as RouteComponent,
  notFoundComponent: NotFoundComponent,
});

// ---------------------------------------------------------------------------
// Index route — root "/" → redirect to correct plane
// ---------------------------------------------------------------------------
export const indexRoute = createRoute({
  getParentRoute: () => emptyLayoutRoute,
  path: '/',
  component: IndexComponent,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getLayoutForRoute = (route: TRouteEntry): TLayoutType => {
  if (route.layout) return route.layout;
  if (route.isPublic === true) return 'empty';
  if (route.requiresAuth === true) return 'default';
  return 'default';
};

const toRouteComponent = (component: React.ComponentType): RouteComponent => {
  if (!component) throw new Error('Route component cannot be null or undefined');
  return component as RouteComponent;
};

const createPermissionGuardedComponent = (
  realComponent: React.ComponentType,
  requiredPermissions: TPermissions
): React.ComponentType => {
  const GuardedComponent: React.FC = () => {
    const { userPermissions } = useRouterContext();
    const registry = AppModuleRegistry.getInstance();
    if (!registry.hasPermission(userPermissions, requiredPermissions)) {
      return createElement(
        Suspense,
        { fallback: createElement(APLoading) },
        createElement(ForbiddenComponent)
      );
    }
    return createElement(
      Suspense,
      { fallback: createElement(APLoading) },
      createElement(realComponent)
    );
  };
  return GuardedComponent;
};

/**
 * Creates a route under a layout, injecting a plane path prefix if provided.
 * The auth guard (requireAuthGuard) is applied via beforeLoad when the route
 * requires authentication — this covers all platform and partner prefixed paths.
 */
const createRouteUnderLayout = (
  routeEntry: TRouteEntry,
  layoutParentFn: () => AnyRoute,
  authClient: AuthClient,
  pathPrefix = ''
) => {
  const component = routeEntry.requiredPermissions
    ? createPermissionGuardedComponent(routeEntry.component, routeEntry.requiredPermissions)
    : routeEntry.component;

  const rawPath = routeEntry.path === '/' ? '/' : routeEntry.path;
  const routeConfig = {
    getParentRoute: layoutParentFn,
    path: pathPrefix ? `${pathPrefix}${rawPath}` : rawPath,
    component: toRouteComponent(component),
  };

  // requireAuthGuard is applied here — it guards the prefixed path (e.g.
  // /platform/users/find), so unauthenticated users hitting any platform or
  // partner route are redirected to login before the component renders.
  if (routeEntry.requiresAuth) {
    return createRoute({ ...routeConfig, beforeLoad: requireAuthGuard(authClient) });
  }
  return createRoute(routeConfig);
};

// ---------------------------------------------------------------------------
// Dynamic route categorization
// ---------------------------------------------------------------------------
type CategorizedRoutes = {
  platformRoutes: TRouteEntry[];
  partnerRoutes: TRouteEntry[];
  emptyLayoutRoutes: TRouteEntry[];
  authLayoutRoutes: TRouteEntry[];
};

const createDynamicRoutesWithLayouts = (userPermissions: TPermissions): CategorizedRoutes => {
  const registry = AppModuleRegistry.getInstance();
  const { publicRoutes, protectedRoutes } = registry.getCategorizedRoutes(userPermissions);

  const platformRoutes: TRouteEntry[] = [];
  const partnerRoutes: TRouteEntry[] = [];
  const emptyLayoutRoutes: TRouteEntry[] = [];
  const authLayoutRoutes: TRouteEntry[] = [];

  [...publicRoutes, ...protectedRoutes].forEach((route) => {
    const layout = getLayoutForRoute(route);

    if (layout === 'empty') {
      emptyLayoutRoutes.push(route);
    } else if (layout === 'auth') {
      authLayoutRoutes.push(route);
    } else {
      // Default layout routes go under platform or partner depending on route ID.
      // isPartnerPlaneRoute checks PARTNER_PLANE_ROUTE_IDS in plane.ts —
      // add new partner route IDs there when the Partner Backoffice grows.
      if (isPartnerPlaneRoute(route.id)) {
        partnerRoutes.push(route);
      } else {
        platformRoutes.push(route);
      }
    }
  });

  return { platformRoutes, partnerRoutes, emptyLayoutRoutes, authLayoutRoutes };
};

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Route tree assembly
// ---------------------------------------------------------------------------
export const createRouteTree = (
  userPermissions: TPermissions = { roles: [] },
  authClient: AuthClient
) => {
  const { platformRoutes, partnerRoutes, emptyLayoutRoutes, authLayoutRoutes } =
    createDynamicRoutesWithLayouts(userPermissions);

  // Platform-plane routes: direct children of defaultLayoutRoute with /platform prefix
  const platformChildren = platformRoutes.map((route) =>
    createRouteUnderLayout(route, () => defaultLayoutRoute, authClient, PLATFORM_PREFIX)
  );

  // Partner-plane routes: direct children of defaultLayoutRoute with /partner prefix
  const partnerChildren = partnerRoutes.map((route) =>
    createRouteUnderLayout(route, () => defaultLayoutRoute, authClient, PARTNER_PREFIX)
  );

  // Public / auth routes (no plane prefix)
  const emptyChildren = emptyLayoutRoutes.map((route) =>
    createRouteUnderLayout(route, () => emptyLayoutRoute, authClient)
  );
  const authChildren = authLayoutRoutes.map((route) =>
    createRouteUnderLayout(route, () => authLayoutRoute, authClient)
  );

  // defaultLayout children = all platform + partner routes (flat, with prefixed paths)
  const defaultWithRoutes = defaultLayoutRoute.addChildren([
    ...platformChildren,
    ...partnerChildren,
  ]);

  // Assemble empty/auth layouts
  const emptyWithChildren = emptyLayoutRoute.addChildren([
    indexRoute,
    ...emptyChildren,
  ]);
  const authWithChildren =
    authChildren.length > 0 ? authLayoutRoute.addChildren(authChildren) : authLayoutRoute;

  return rootRoute.addChildren([defaultWithRoutes, emptyWithChildren, authWithChildren]);
};

// ---------------------------------------------------------------------------
// Router factory
// ---------------------------------------------------------------------------
export const createAppRouter = (
  userPermissions: TPermissions = { roles: [] },
  authClient: AuthClient
) => {
  const routeTree = createRouteTree(userPermissions, authClient);

  return createRouter({
    routeTree,
    basepath: PANEL_BASEPATH,
    defaultPendingComponent: APLoading,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    context: {
      userPermissions,
      authClient,
    },
  });
};

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
